import {
  aggregateTextInlineStyle,
  textBaseStyleOf,
  type TextCanvasObject,
} from "@refinement-canvas/domain";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import type { CanvasOpDispatcher } from "./use-canvas-op-dispatcher";

// 選択した文字範囲の太字について、現在値の読み取りと切り替えの適用を 1 か所へ集約する。
//
// 入口は 2 つ（範囲専用パネルの `B` と、本文編集中の Ctrl/Command+B）あり、どちらも同じ結果に
// なる必要がある。判定と適用をそれぞれの入口へ書くと「パネルでは解除なのにショートカットでは
// 太字になる」といった食い違いが生まれるため、本モジュールを共通の正本とする。
//
// 置き場所を features/canvas 直下にしているのは、ツールバー（toolbar/）と入力（input/）の
// 両方から使うためで、片方のディレクトリへ置くともう片方からの依存が逆流する。

/** 太字の切り替えに渡す選択範囲（`text` は選択した時点の本文全体）。 */
interface BoldTargetRange {
  readonly end: number;
  readonly start: number;
  readonly text: string;
}

/**
 * 選択範囲の太字の現在値。範囲内で値が混在していれば null を返す。
 * 判定は「文字ごとの override ?? 図形の基底書式」で、範囲専用パネルの表示と同じ集計を使う。
 */
export function readTextRangeBold(
  object: TextCanvasObject,
  range: { readonly start: number; readonly end: number }
): boolean | null {
  const aggregate = aggregateTextInlineStyle({
    base: textBaseStyleOf(object),
    end: range.end,
    runs: object.inlineStyles,
    start: range.start,
    text: object.text,
  });
  return aggregate.bold.mixed ? null : (aggregate.bold.value ?? false);
}

/**
 * 選択範囲の太字を切り替える。現在値が true のときだけ解除し、
 * それ以外（false / 混在）は全範囲を太字へそろえる（範囲専用パネルの `B` と同じ規則）。
 * 送信が成立したら true を返す（呼び出し側が編集中 Undo 履歴への通知を出す）。
 */
export function toggleTextRangeBold(params: {
  readonly dispatcher: CanvasOpDispatcher;
  readonly objectId: string;
  readonly range: BoldTargetRange;
}): boolean {
  // 集計は doc store の権威オブジェクトから行う。updateTextRangeStyle は権威本文との一致
  // （current.text !== range.text なら送らない）を条件にしているため、集計と送信で別の本文を
  // 見ると判定の根拠がずれる。
  const current = useCanvasDocStore.getState().objects[params.objectId];
  if (!current || current.type !== "text" || current.locked === true) return false;
  const bold = readTextRangeBold(current, params.range);
  return params.dispatcher.updateTextRangeStyle(params.objectId, params.range, {
    bold: bold !== true,
  });
}
